"use client";

import {
  Info,
  OctagonAlert,
  TriangleAlert,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  bannerToShow,
  type SiteBanner as SiteBannerData,
} from "@/lib/banner/schema";
import { isShellUserAgent } from "@/lib/shell";
import { cn } from "@/lib/utils";

const ENDPOINT = "/api/site-banner";
const DISMISS_STORAGE_KEY = "site-banner-dismissed";

// Set the height var before paint on the client; no-op on the server.
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

const SEVERITY_CLASS: Record<SiteBannerData["severity"], string> = {
  info: "bg-muted text-foreground",
  warning: "bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-100",
  critical: "bg-destructive/10 text-destructive dark:bg-destructive/15",
};

const SEVERITY_ICON: Record<SiteBannerData["severity"], LucideIcon> = {
  info: Info,
  warning: TriangleAlert,
  critical: OctagonAlert,
};

/** Site-wide announcement / outage / maintenance bar. Fetches the active banner
 *  once from /api/site-banner (which reads the PostHog `site-banner` flag
 *  payload server-side) and renders nothing when there's none or it was
 *  dismissed. A raised banner appears on the next navigation. Rendered through
 *  `SiteBanner`, which decides `hideAppPromotion`. */
export function SiteBannerClient({
  hideAppPromotion = false,
}: {
  hideAppPromotion?: boolean;
}) {
  const [banner, setBanner] = useState<SiteBannerData | null>(null);
  const [dismissedId, setDismissedId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await fetch(ENDPOINT);
        if (!active || !res.ok) return;
        const data = (await res.json()) as { banner: SiteBannerData | null };
        if (!active) return;
        setBanner(
          bannerToShow(data.banner, {
            inShell: isShellUserAgent(navigator.userAgent),
            hideAppPromotion,
          }),
        );
        try {
          setDismissedId(window.localStorage.getItem(DISMISS_STORAGE_KEY));
        } catch {
          // localStorage unavailable — treat as nothing dismissed.
        }
      } catch {
        // Network error — leave the banner unset.
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [hideAppPromotion]);

  const visible =
    banner !== null && !(banner.dismissable && dismissedId === banner.id);

  // Publish the rendered height as `--site-banner-h` so viewport-height layouts
  // (scenario player, preceptor chat) can subtract it; 0px when hidden. The
  // ResizeObserver keeps it correct when the message rewraps (resize/rotation/
  // font load), which deps alone can't catch.
  useIsomorphicLayoutEffect(() => {
    const root = document.documentElement;
    const el = visible ? ref.current : null;
    if (!el) {
      root.style.setProperty("--site-banner-h", "0px");
      return;
    }
    const update = () =>
      root.style.setProperty("--site-banner-h", `${el.offsetHeight}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.setProperty("--site-banner-h", "0px");
    };
  }, [visible, banner?.id]);

  if (banner === null || !visible) return null;

  const handleDismiss = () => {
    setDismissedId(banner.id);
    try {
      window.localStorage.setItem(DISMISS_STORAGE_KEY, banner.id);
    } catch {
      // Storage unavailable — dismissal holds for this session only.
    }
  };

  // Open external links in a new tab so a "status page" link doesn't unload
  // the app mid-session; in-app links navigate normally.
  const isExternal =
    banner.linkHref?.startsWith("http://") ||
    banner.linkHref?.startsWith("https://");

  const Icon = SEVERITY_ICON[banner.severity];

  return (
    <div
      ref={ref}
      role={banner.severity === "critical" ? "alert" : "status"}
      aria-live={banner.severity === "critical" ? "assertive" : "polite"}
      className={cn(
        "relative flex items-center justify-center gap-2 border-b px-10 py-2 text-sm",
        SEVERITY_CLASS[banner.severity],
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <p className="text-center whitespace-pre-line">
        {banner.message}
        {banner.linkHref && banner.linkText ? (
          <a
            href={banner.linkHref}
            {...(isExternal
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
            className="ml-1.5 font-medium underline underline-offset-2"
          >
            {banner.linkText}
          </a>
        ) : null}
      </p>
      {banner.dismissable ? (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss announcement"
          className="absolute top-1/2 right-3 -translate-y-1/2 rounded-sm opacity-80 transition-opacity hover:opacity-100"
        >
          <X className="size-4" />
        </button>
      ) : null}
    </div>
  );
}
